/* jshint esversion: 6 */
import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { MyContext } from "../../Context/MyProvider";
import { showToast } from "../../showTost";

function parseYmd(value) {
  const s = String(value).slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d, iso: s };
}

function formatDateLabel(value) {
  const p = parseYmd(value);
  if (!p) return String(value).slice(0, 10);
  const dt = new Date(p.y, p.m - 1, p.d);
  return dt.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}

function formatTabLabel(value) {
  const p = parseYmd(value);
  if (!p) return String(value).slice(0, 10);
  const dt = new Date(p.y, p.m - 1, p.d);
  const day = dt.toLocaleDateString("en-IN", { weekday: "short" });
  const rest = dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  return { day, rest };
}

function slotKey(slot) {
  if (typeof slot === "object" && slot?.start) return `${slot.start}-${slot.end}`;
  return String(slot);
}

function slotLabel(slot) {
  if (typeof slot === "object" && slot?.start) return `${slot.start}–${slot.end}`;
  return String(slot);
}

export default function LabTestsSlots() {
  const { ApiBaseUrl } = useContext(MyContext);
  const navigate = useNavigate();
  const [phlebs, setPhlebs] = useState([]);
  const [activeDate, setActiveDate] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSlots = () => {
    setLoading(true);
    setError("");
    const loc = { latitude: 17.385, longitude: 78.4867, city: "Hyderabad" };
    const from = new Date().toISOString().slice(0, 10);
    const to = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
    axios.post(`${ApiBaseUrl}/api/v1/lab/phlebotomist-availability`, {
      user_location: loc, date_from: from, date_to: to, preferred_time: "anytime",
    })
      .then((res) => {
        if (res.data.success) {
          const list = res.data.phlebotomists || [];
          setPhlebs(list);
          const dates = [];
          list.forEach((p) => {
            (p.available_dates || []).forEach((d) => {
              const iso = String(d.date).slice(0, 10);
              if (!dates.includes(iso)) dates.push(iso);
            });
          });
          dates.sort();
          setActiveDate((prev) => prev || dates[0] || "");
          if (!list.length) setError("No home-collection slots in your area right now. Try again tomorrow.");
        } else {
          setError(res.data.error_message || "No slots available");
        }
      })
      .catch((e) => setError(e.response?.data?.error_message || "Could not load slots. Check your connection."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    try {
      const cart = JSON.parse(sessionStorage.getItem("lab_tests_cart") || "[]");
      if (!cart.length) {
        showToast({ title: "Select tests first", id: "warning" });
        navigate("/lab-tests", { replace: true });
        return;
      }
    } catch (_) {
      navigate("/lab-tests", { replace: true });
      return;
    }
    loadSlots();
  }, [ApiBaseUrl, navigate]);

  const dateTabs = useMemo(() => {
    const dates = [];
    phlebs.forEach((p) => {
      (p.available_dates || []).forEach((d) => {
        const iso = String(d.date).slice(0, 10);
        if (!dates.includes(iso)) dates.push(iso);
      });
    });
    return dates.sort();
  }, [phlebs]);

  useEffect(() => {
    if (dateTabs.length && activeDate && !dateTabs.includes(activeDate)) {
      setActiveDate(dateTabs[0]);
    }
  }, [dateTabs, activeDate]);

  const pickDateTab = (iso) => {
    setActiveDate(iso);
    setSelected(null);
  };

  const phlebsForDate = useMemo(() => {
    if (!activeDate) return [];
    return phlebs
      .map((p) => {
        const day = (p.available_dates || []).find((d) => String(d.date).slice(0, 10) === activeDate);
        if (!day?.available_slots?.length) return null;
        return { ...p, slots: day.available_slots };
      })
      .filter(Boolean);
  }, [phlebs, activeDate]);

  const goCheckout = () => {
    if (!selected) {
      showToast({ title: "Tap a time slot to continue", id: "warning" });
      return;
    }
    sessionStorage.setItem("lab_slot", JSON.stringify(selected));
    navigate("/lab-tests/checkout", { state: { slot: selected } });
  };

  return (
    <div className="width-container py-8 pb-56">
      <Link to="/lab-tests" className="text-sm text-rm-primary underline mb-4 inline-block">← Back to tests</Link>
      <h1 className="text-2xl font-bold mb-2">Choose collection slot</h1>
      <p className="text-gray-600 mb-4">Pick a date, then tap a time. A verified phlebotomist visits your home in Hyderabad.</p>

      {loading && <p className="text-gray-500 py-8 text-center">Loading available slots…</p>}

      {!loading && error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl mb-4">
          <p className="text-red-800 mb-3">{error}</p>
          <button type="button" className="rm-btn-primary px-4 py-2" onClick={loadSlots}>Retry</button>
        </div>
      )}

      {!loading && !error && dateTabs.length > 0 && (
        <>
          <div className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-1 px-1 lab-date-tabs">
            {dateTabs.map((iso) => {
              const { day, rest } = formatTabLabel(iso);
              const active = activeDate === iso;
              return (
                <button
                  key={iso}
                  type="button"
                  onClick={() => pickDateTab(iso)}
                  className={`flex-shrink-0 min-w-[72px] rounded-xl border px-3 py-2 text-center transition-colors ${active ? "bg-rm-primary text-white border-rm-primary shadow-sm" : "bg-white border-gray-200 hover:border-rm-primary"}`}
                >
                  <span className="block text-xs font-medium opacity-90">{day}</span>
                  <span className="block text-sm font-bold">{rest}</span>
                </button>
              );
            })}
          </div>

          {phlebsForDate.map((p) => (
            <div key={`${p.phlebotomist_id}-${activeDate}`} className="border rounded-xl p-4 mb-4 bg-white shadow-sm">
              <div className="flex gap-4 items-center mb-4">
                {p.photo_url && <img src={p.photo_url} alt="" className="w-14 h-14 rounded-full object-cover" />}
                <div>
                  <h3 className="font-semibold text-lg">{p.name}</h3>
                  <p className="text-sm text-gray-500">{p.qualification} · ★ {p.rating}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {p.slots.map((slot) => {
                  const key = `${p.phlebotomist_id}-${activeDate}-${slotKey(slot)}`;
                  const active = selected?.key === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`text-sm px-3 py-3 rounded-lg border font-medium transition-colors ${active ? "bg-rm-primary text-white border-rm-primary" : "bg-white hover:border-rm-primary hover:bg-teal-50"}`}
                      onClick={() => setSelected({
                        key,
                        phlebotomist_id: p.phlebotomist_id,
                        appointment_date: activeDate,
                        appointment_slot: slotKey(slot),
                        phlebotomist_name: p.name,
                      })}
                    >
                      {slotLabel(slot)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {!phlebsForDate.length && (
            <p className="text-gray-600 py-4">No slots on this date. Try another day.</p>
          )}
        </>
      )}

      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t p-4 shadow-lg z-[1300] safe-bottom">
        <div className="width-container max-w-3xl mx-auto pointer-events-auto">
          {selected ? (
            <p className="text-sm text-gray-600 mb-2">
              Selected: <strong>{selected.phlebotomist_name}</strong> · {formatDateLabel(selected.appointment_date)} · {selected.appointment_slot.replace("-", "–")}
            </p>
          ) : (
            <p className="text-sm text-amber-700 mb-2">Select a date tab and time slot above</p>
          )}
          <button
            type="button"
            className="rm-btn-primary w-full py-3 text-base font-semibold disabled:opacity-50"
            disabled={!selected}
            onClick={goCheckout}
          >
            Continue to checkout
          </button>
        </div>
      </div>
    </div>
  );
}
